import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export default function VoiceButton({ onTranscript, className = '' }) {
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef(null);
    const onTranscriptRef = useRef(onTranscript);

    // Keep callback ref in sync without triggering toggle recreation
    useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);

    const toggle = useCallback(() => {
        if (listening) {
            recognitionRef.current?.stop();
            setListening(false);
            return;
        }
        if (!SpeechRecognition) {
            toast.error('Voice input not supported in this browser');
            return;
        }
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = 'en-US';
        recognition.onresult = (event) => {
            let transcript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) transcript += event.results[i][0].transcript;
            }
            if (transcript) onTranscriptRef.current(transcript.trim());
        };
        recognition.onerror = (e) => {
            if (e.error === 'not-allowed') {
                toast.error('Microphone access denied. Check browser permissions.');
            } else if (e.error !== 'aborted') {
                toast.error(`Voice error: ${e.error}`);
            }
            setListening(false);
        };
        recognition.onend = () => setListening(false);
        recognitionRef.current = recognition;
        recognition.start();
        setListening(true);
    }, [listening]); // removed onTranscript — using ref instead

    if (!SpeechRecognition) return null;

    return (
        <button type="button" onClick={toggle} title={listening ? 'Stop recording' : 'Voice input'}
            className={`p-1.5 rounded-lg transition-colors ${listening ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700'} ${className}`}>
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
    );
}
